import { useScroll } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import useScreenSize from "~/hook/useScreenSize";
import { setHoveredChar, setIsTouchBar } from "~/lib/store/slice/kirtanLyricsSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { LANG } from "~/utils/constant";

type PropsType = {
  kirtanCharacters: string[];
};

export const KirtanCharactersScrollBar: React.FC<PropsType> = ({ kirtanCharacters }) => {
  const { hoveredChar } = useAppSelector((state) => state.kirtanLyrics);
  const dispatch = useAppDispatch();
  const lang = useParams()?.lang || LANG.english;
  const screenSize = useScreenSize();
  const { scrollY } = useScroll();
  const [showTopButton, setTopButton] = useState(false);

  const [isTouching, setIsTouching] = useState(false);

  const handleRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const charRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const unsubscribe = scrollY.onChange((latestScrollY) => {
      setTopButton(latestScrollY > 250);
    });

    return () => {
      unsubscribe();
    };
  }, [scrollY]);

  const fetchUntilCharFound = async (char: string) => {
    dispatch(setHoveredChar(char));
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setIsTouching(true);
    dispatch(setIsTouchBar(true));
    const touch = e.touches[0];
    if (!touch || !sliderRef.current || !handleRef.current) return;

    const sliderRect = sliderRef.current.getBoundingClientRect(); // Get the bounds of the slider
    const touchY = touch.clientY - sliderRect.top; // Get the vertical touch position relative to the slider

    // Limit the top position of the handle within the slider's bounds
    const handleHeight = handleRef.current.getBoundingClientRect().height;
    const minTop = 0; // The handle cannot go above the top of the slider
    const maxTop = sliderRect.height - handleHeight; // The handle cannot go below the bottom of the slider

    // Set the top value to ensure the handle stays within the bounds
    const constrainedTop = Math.min(Math.max(touchY, minTop), maxTop);

    // Move the handle while maintaining the constraints
    handleRef.current.style.top = `${constrainedTop}px`;

    // Check which character is hovered and update the hoveredChar
    for (const char of kirtanCharacters) {
      const el = charRefs?.current[char];
      if (!el) continue;

      const rect = el.getBoundingClientRect();
      const isHovering = touch.clientY >= rect.top && touch.clientY <= rect.bottom;

      if (isHovering) {
        setHoveredChar(char);
        dispatch(setHoveredChar(char));
        break;
      }
    }
  };

  const onTouchEnd = () => {
    if (hoveredChar) {
      fetchUntilCharFound(hoveredChar);
    }
    setHoveredChar(null);
    setIsTouching(false);
    dispatch(setIsTouchBar(false));
  };

  const getContainerHeight = () => {
    const charCount = kirtanCharacters?.length || 0;

    if (typeof window === "undefined") {
      return screenSize === "desktop" ? 600 : 400;
    }

    let fullHeight;
    if (screenSize === "desktop") {
      const screenHeight = window.innerHeight;
      const isLaptopScreen = screenHeight <= 900;

      if (isLaptopScreen) {
        fullHeight = showTopButton ? screenHeight * 0.8 : screenHeight * 0.73;
      } else {
        fullHeight = showTopButton ? screenHeight * 0.9 : screenHeight * 0.73;
      }

      const minHeight = 500;
      const maxHeight = isLaptopScreen ? screenHeight * 0.8 : screenHeight * 0.9;
      fullHeight = Math.max(minHeight, Math.min(fullHeight, maxHeight));
    } else {
      const mobileViewportHeight = window.visualViewport?.height || window.innerHeight;
      fullHeight = showTopButton
        ? screenSize === "laptop"
          ? mobileViewportHeight * 0.85
          : mobileViewportHeight * 0.7
        : screenSize === "laptop"
          ? mobileViewportHeight * 0.6
          : mobileViewportHeight * 0.75;
    }

    if (screenSize === "desktop") {
      const padding = 32;
      const minItemHeight = 20;
      const requiredHeight = charCount * minItemHeight + padding;
      // Math.min(requiredHeight, fullHeight)
      return fullHeight;
    } else {
      const padding = 32;
      const minItemHeight = 24;
      const requiredHeight = charCount * minItemHeight + padding;
      return requiredHeight <= fullHeight ? fullHeight : Math.min(requiredHeight, fullHeight);
    }
  };

  return (
    <div
      ref={sliderRef}
      className={cn(
        "relative w-auto  bg-secondaryColor rounded-full flex flex-col flex-shrink justify-between items-center py-4 touch-none cursor-pointer",
        lang === LANG.gujarati ? "kirtan_container_gujarati" : "kirtan_container",
      )}
      // style={{ height: `${getContainerHeight()}px` }}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {kirtanCharacters?.map((char) => (
        <div
          className="flex items-center space-x-2 cursor-pointer w-full px-2 group relative py-0.5"
          key={char}
          ref={(el) => (charRefs.current[char] = el)}
          onClick={() => fetchUntilCharFound(char)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fetchUntilCharFound(char);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`Filter by character ${char}`}
        >
          {/* Dot */}
          <div className="relative flex justify-center items-center">
            <div
              className={cn(
                "size-1.5 lg:size-[0.233rem] 2xl:size-1.5 rounded-full cursor-pointer",
                char === hoveredChar ? "bg-textHeadingColor" : "bg-neutral-300",
              )}
            ></div>
          </div>

          {/* Tooltip  */}
          <div
            className={cn(
              "absolute top-1/2 right-8 transform -translate-y-1/2 size-14 rounded-full bg-primaryColor z-50 text-white font-inter font-semibold text-[20px] flex items-center justify-center opacity-0 transition-all duration-200 pointer-events-none",
              hoveredChar === char && isTouching ? "opacity-100" : "md:group-hover:opacity-100",
            )}
          >
            <span className="capitalize text-white">{char}</span>
          </div>
        </div>
      ))}

      {/* Draggable handle */}
      {isTouching && (
        <div
          ref={handleRef}
          className="absolute left-1/2 transform -translate-x-1/2 w-full h-8 rounded-full bg-primaryColor z-30 touch-none md:hidden opacity-50"
          style={{ top: 0 }}
        />
      )}
    </div>
  );
};
